import { cn } from "@/lib/utils";
import { pushVector } from "@/rc/drivetrains";
import "./rc.css";

const SPIN_ARC = "M -8 -13.86 A 16 16 0 1 1 -13.86 8";
const SPIN_HEAD = "M -16.4 3.7 L -10.4 6 L -17.3 10 Z";
const centre = (b) => (b.type === "rect" ? [b.x + b.w / 2, b.y + b.h / 2] : [100, 92]);

const Body = ({ body }) => (body.type === "rect"
  ? <rect className="rc-body" x={body.x} y={body.y} width={body.w} height={body.h} rx="14" />
  : <polygon className="rc-body" points={body.points} strokeLinejoin="round" />);

const Hub = ({ at: [x, y] }) => (
  <g transform={`translate(${x - 15} ${y - 20})`}>
    <rect className="rc-hub" width="30" height="40" rx="6" />
    {[0, 1, 2].flatMap((r) => [0, 1, 2].map((c) => <circle key={`${r}${c}`} className="rc-hub-dot" cx={8 + c * 7} cy={13 + r * 7} r="1.6" />))}
  </g>
);

const Wheel = ({ w, steer, omni, active }) => (
  <g transform={`translate(${w.x} ${w.y}) rotate(${w.rot})`}>
    <g className={steer ? "rc-wheel-steer" : undefined}>
      <rect className={cn("rc-wheel", active && "is-active")} x="-8" y="-17" width="16" height="34" rx="4" />
      {omni
        ? [-11, -5.5, 0, 5.5, 11].map((y) => <line key={y} className="rc-roller" x1="-5" x2="5" y1={y} y2={y} />)
        : <line className="rc-tread" x1="0" x2="0" y1="-14" y2="14" />}
    </g>
  </g>
);

const PushArrow = ({ x, y, vec: [hx, hy] }) => {
  const [ux, uy] = [hx, -hy];
  const [tx, ty] = [x + ux * 32, y + uy * 32];
  const [bx, by] = [tx - ux * 9, ty - uy * 9];
  const [nx, ny] = [-uy * 5.5, ux * 5.5];
  return (
    <g className="rc-push">
      <line x1={x} y1={y} x2={bx} y2={by} />
      <path d={`M ${tx} ${ty} L ${bx + nx} ${by + ny} L ${bx - nx} ${by - ny} Z`} />
    </g>
  );
};

const SpinArrow = ({ at: [x, y], reverse, testId }) => (
  <g transform={`translate(${x} ${y}) scale(${reverse ? -1 : 1} 1)`} className="rc-spin" data-testid={testId} data-dir={reverse ? "anticlockwise" : "clockwise"}>
    <path d={SPIN_ARC} />
    <path d={SPIN_HEAD} className="rc-spin-head" />
  </g>
);

const Badge = ({ m, port, active, conflict }) => (
  <g transform={`translate(${m.badge[0]} ${m.badge[1]})`}>
    {active && <circle r="17" className="rc-badge-ring" />}
    <circle r="11" className={cn("rc-badge", m.steer && "is-steer", conflict && "is-conflict")} />
    {port
      ? <text className={cn("rc-badge-text", m.steer && "is-steer")} y="4.5" textAnchor="middle">{port}</text>
      : <path className="rc-badge-axle" d="M -4 0 H 4 M 0 -4 V 4" />}
  </g>
);

// Top-down robot drawing. "ports" mode adds port letters plus spin arrows (tank/car) or push arrows (holonomic).
export const RobotDiagram = ({ dt, cfg, mode = "preview", active, conflicts, live = false, className }) => {
  const ports = mode === "ports";
  const motorOf = (id) => dt.motors.find((m) => m.id === id);
  const [fx, fy] = dt.front;
  return (
    <svg viewBox="0 0 200 200" className={cn("overflow-visible", live && "rc-live", className)} role="img" aria-label={`${dt.name}, seen from above with the front at the top`} data-testid={`rc-diagram-${dt.id}`}>
      <g className={`rc-robot rc-anim-${dt.anim}`}>
        <Body body={dt.body} />
        <Hub at={centre(dt.body)} />
        {dt.links.map(([x1, y1, x2, y2]) => <line key={`${x1}-${y1}`} className="rc-link" x1={x1} y1={y1} x2={x2} y2={y2} />)}
        {dt.wheels.map((w) => <line key={`wire-${w.x}-${w.y}`} className="rc-wire" x1={w.x} y1={w.y} x2={motorOf(w.motor).badge[0]} y2={motorOf(w.motor).badge[1]} />)}
        {dt.wheels.map((w) => <Wheel key={`${w.x}-${w.y}`} w={w} steer={motorOf(w.motor).steer} omni={dt.kind === "omni"} active={ports && active === w.motor} />)}
        <path className="rc-front" d={`M ${fx - 7} ${fy + 4} L ${fx} ${fy - 5} L ${fx + 7} ${fy + 4} Z`} />
        {ports && dt.kind === "omni" && dt.wheels.map((w) => <PushArrow key={`push-${w.x}-${w.y}`} x={w.x} y={w.y} vec={pushVector(motorOf(w.motor), cfg.motors[w.motor].dir)} />)}
        {ports && dt.kind !== "omni" && dt.motors.map((m) => <SpinArrow key={m.id} at={m.badge} reverse={cfg.motors[m.id].dir === "R"} testId={`rc-spin-${m.id}`} />)}
        {dt.motors.map((m) => (
          <Badge key={m.id} m={m} port={ports ? cfg.motors[m.id].port : null} active={ports && active === m.id} conflict={ports && conflicts?.has(cfg.motors[m.id].port)} />
        ))}
      </g>
    </svg>
  );
};
